import Link from "next/link";
import orderBy from "lodash/orderBy";

import { useSelector } from "react-redux";
import { useEffect, useState, useContext } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import isEmpty from "lodash/isEmpty";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useJuneSoEventTracking } from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";
import { getMenu } from "@selectors/menu";
import { getIsLockerShop, getIsPricelessShop } from "@selectors/store";
import { useMenuAnalytics } from "@hooks/analytics/use-menu-analytics";

import { checkIfMenuExists, getMenuItemImage } from "@helpers/menu";
import { getMenuItemRoute, ALL_PRODUCTS } from "@constants/routes";
import { useStoreContext } from "@components/store/provider";
import { InternalButton } from "@sharedComponents/buttons";
import DummyStoreCategories from "@components/dummy-store/menu-categories";

import Multimedia from "@sharedComponents/multimedia";

import {
  MENU_CATALOG_SELECTION,
  PRICELESS_SHOP,
  SHOP_CREATION,
  SHOP_CREATION_FLOW,
} from "@constants/june-so";
import { PREDEFINED_MENU_ITEMS } from "@constants/menu";
import NoImage from "@assets/images/no-image.svg";

import ddStyles from "./main.module.scss";

const SINGLE_LEVEL_MENU_THRESHOLD = 7;
/*CENTER_MENU_THRESHOLD to avoid page horizontal scroll with 3 tier menu and 6 menu per column is defined in css*/
const CENTER_MENU_THRESHOLD = 12;

const Menu = ({ expanded, navContainerClass, setMenuVisibility, children }) => {
  const router = useRouter();
  const { query } = router;
  const { trackMainCategory, trackSubcategory } = useMenuAnalytics();

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const [activeParentMenuId, setActiveParentMenuId] = useState();
  const [isEditCategoriesModalOpen, setIsEditCategoriesModalOpen] = useState(false);
  const {
    storeBasePath,
    persistedFiltersQuery,
    isPreviewCatalogMode,
    category,
    subCategory,
    countryCode,
  } = useStoreContext();
  const orderedMenuItem = useSelector(getMenu);
  const isLockerShop = useSelector(getIsLockerShop);

  const isPricelessShop = useSelector(getIsPricelessShop);
  const { trackEvent } = useJuneSoEventTracking({ services: SHOP_CREATION_FLOW });

  const isProductListingPage = !!query.category;

  const { isNewShopCreationFlow } = useCreateShopContext();

  const openEditCategoriesModal = () => setIsEditCategoriesModalOpen(true);
  const closeEditCategoriesModal = () => setIsEditCategoriesModalOpen(false);

  useEffect(() => {
    if (!expanded) setActiveParentMenuId(null);
  }, [expanded]);

  const resetActiveMenuItemId = (e) => {
    e.stopPropagation();
    closeMenu();
    setActiveParentMenuId(null);
  };

  const menuList = orderedMenuItem?.children || [];
  const menuListLength = menuList.length;
  const isMultiLevelMenuListingRequired = menuListLength - SINGLE_LEVEL_MENU_THRESHOLD > 2;

  const shouldSkipAllProductsMenuListing = (menuItem) =>
    !isMobileDevice &&
    isMultiLevelMenuListingRequired &&
    menuItem.code === ALL_PRODUCTS &&
    !isLockerShop;

  useEffect(() => {
    if (
      menuList.length > 0 &&
      category &&
      subCategory &&
      !checkIfMenuExists(menuList, category, subCategory)
    ) {
      router.replace(
        getMenuItemRoute({
          storeBasePath,
          category: menuList[0].code,
          query: persistedFiltersQuery,
        }),
      );
    }
  }, [countryCode, menuList, category, subCategory]);

  //to automatically close category dropdown
  const blurActiveElement = () => document.activeElement?.blur();

  const onDropdownItemClick = (menuItem) => {
    const id = activeParentMenuId ? null : menuItem.id;
    blurActiveElement();
    setActiveParentMenuId(id);
    if (!isMobileDevice) {
      trackMainCategory(menuItem);
      router.push(
        getMenuItemRoute({
          storeBasePath,
          category: menuItem.code,
          query: persistedFiltersQuery,
        }),
      );
    }
  };

  const renderAllItemLink = (menuItem, className = " ") => {
    return (
      <div key={`dropdown-${menuItem.id}`} className={className} onClick={resetActiveMenuItemId}>
        <Link
          onClick={() => {
            trackMainCategory(menuItem);
            closeMenu();
          }}
          href={getMenuItemRoute({
            storeBasePath,
            category: menuItem.code,
            query: persistedFiltersQuery,
          })}
          prefetch={false}
          className={ddStyles.navLink}
        >
          <span>
            Shop All <span className={ddStyles.shopAllItemName}>{menuItem.name}</span>
          </span>
          <i className={ddStyles.iconArrowRight} />
        </Link>
      </div>
    );
  };

  const closeMenu = () => {
    setMenuVisibility(false);
    blurActiveElement();
  };

  const renderDropdownItem = (menuItem, parentItem) => {
    let className = `nav-link ${ddStyles.navLinkBlock} `;
    className += menuItem.code === subCategory ? "active t-bg-4" : "";

    return (
      <dd key={`dropdown-${menuItem.id}`} className={className} onClick={resetActiveMenuItemId}>
        <Link
          href={getMenuItemRoute({
            storeBasePath,
            category: menuItem.code,
            query: persistedFiltersQuery,
          })}
          onClick={() => {
            trackSubcategory(parentItem, menuItem);
            blurActiveElement();
          }}
          className={ddStyles.navLink}
          title={""}
          prefetch={false}
        >
          <span className={`${ddStyles.linkImg}`}>
            <Multimedia
              src={getMenuItemImage(menuItem.attachment) || NoImage}
              type="image"
              className="rounded"
              alt={menuItem.name}
              height={40}
              width={40}
              loading="lazy"
            />
          </span>
          <span>{menuItem.name}</span>
        </Link>
      </dd>
    );
  };

  const renderNoChildCategory = (menuItem, { className = "", withIcon = false }) => {
    return (
      <li
        id="basic-nav-dropdown"
        key={`navlink-${menuItem.id}`}
        className={` ${className} ${ddStyles.categoryDropdDown} ${ddStyles.categoryDropDownList} ${ddStyles.categoryWithAccessLink} ${ddStyles.singleLevel}`}
      >
        <Link
          passHref
          href={getMenuItemRoute({
            storeBasePath,
            category: menuItem.code,
            query: persistedFiltersQuery,
          })}
          className={`${ddStyles.navLink} ${ddStyles.accessLink}`}
          title={""}
          prefetch={false}
          onClick={(e) => {
            trackMainCategory(menuItem);
            resetActiveMenuItemId(e);
          }}
        >
          {withIcon && (
            <span className={ddStyles.linkImg}>
              <Multimedia
                isNextImage={false}
                src={getMenuItemImage(menuItem.attachment) || NoImage}
                type="image"
                className="rounded"
                alt={menuItem.name}
                height={40}
                width={40}
                loading="lazy"
              />
            </span>
          )}
          <span>{menuItem.name}</span>
          <span className="active-nav-link-indicator t-bg-1"></span>
        </Link>
      </li>
    );
  };

  const renderSingleLevelMenuListing = (menuItem, index) => {
    if (shouldSkipAllProductsMenuListing(menuItem)) return;

    let className = `nav-link ${ddStyles.storeNavLink} `;
    className += menuItem.code === category ? "active t-bg-4 " : "";
    className +=
      activeParentMenuId === menuItem.id ? ` ${ddStyles.clicked} mb-clicked t-cl-1` : " ";

    const merchCategory = menuItem.forSwag && !isLockerShop && isPreviewCatalogMode;

    if (
      (!isEmpty(menuItem.linkedTaxonResources) && menuItem.children.length === 0) ||
      PREDEFINED_MENU_ITEMS.includes(menuItem.code) ||
      merchCategory ||
      menuItem.isDummyStoreMerch
    ) {
      return renderNoChildCategory(menuItem, { className, index });
    } else if (menuItem.children.length > 0) {
      return (
        <li
          id="basic-nav-dropdown"
          key={`navlink-${menuItem.id}`}
          className={` ${className} ${ddStyles.categoryDropdDown} ${
            ddStyles.categoryDropDownList
          } ${ddStyles.categoryWithAccessLink} ${
            menuItem.children.length > CENTER_MENU_THRESHOLD ? ddStyles.centerMenu : ""
          }`}
        >
          <InternalButton
            onClick={() => onDropdownItemClick(menuItem)}
            styleType="no-style"
            className={ddStyles.accessLink}
          >
            <span className={ddStyles.mobileClickedOnly}>
              <i className={`${ddStyles.iconArrowleft} ${ddStyles.mobileIcon}`} />
            </span>
            {menuItem.name}
            <span
              className={`${ddStyles.selectedNavBorder} active-nav-link-indicator t-bg-1`}
            ></span>
            <i className={`${ddStyles.iconArrowRight} ${ddStyles.mobileIcon}`} />
          </InternalButton>
          <div className={`${ddStyles.categoryDropdDownMenu} ${ddStyles.categoryDropDownMenu}`}>
            <dl className={ddStyles.menuItemList}>
              {menuItem.children.map((item) => renderDropdownItem(item, menuItem))}
            </dl>
            {renderAllItemLink(menuItem, ddStyles.categoryAllLink)}
          </div>
        </li>
      );
    }
  };

  const renderMultiLevelMenuListing = (menuItem) => {
    if (shouldSkipAllProductsMenuListing(menuItem)) return;

    let className = `nav-link ${ddStyles.storeNavLink} `;
    className += menuItem.code === category ? "active t-bg-4 " : "";
    className +=
      activeParentMenuId === menuItem.id ? ` ${ddStyles.clicked} mb-clicked t-cl-1` : " ";
    if (menuItem.children.length > 0)
      return (
        <dl className={ddStyles.menuItemList} key={`${menuItem.id}-menu-multi-level`}>
          <dd className={ddStyles.navLinkBlock}>
            <InternalButton
              styleType="no-style"
              className={ddStyles.navLink}
              onClick={() => onDropdownItemClick(menuItem)}
              onKeyDown={() => onDropdownItemClick(menuItem)}
            >
              <span className={ddStyles.linkImg}>
                <Multimedia
                  isNextImage={false}
                  src={getMenuItemImage(menuItem.attachment) || NoImage}
                  type="image"
                  className="rounded"
                  alt={menuItem.name}
                  height={40}
                  width={40}
                  loading="lazy"
                />
              </span>
              <span className={ddStyles.menuItemName}>{menuItem.name}</span>
              <i className={ddStyles.iconArrowRight} />
            </InternalButton>

            <article className={`${ddStyles.secondLevelMenu} ${ddStyles.categoryDropdDownMenu}`}>
              <dl className={ddStyles.menuItemList}>
                {menuItem.children.map((item) => renderDropdownItem(item, menuItem))}
              </dl>
              {renderAllItemLink(menuItem, ddStyles.categoryAllLink)}
            </article>
          </dd>
        </dl>
      );
    else if (
      !isEmpty(menuItem.linkedTaxonResources) ||
      PREDEFINED_MENU_ITEMS.includes(menuItem.code) ||
      menuItem.isDummyStoreMerch
    ) {
      return renderNoChildCategory(menuItem, { className, withIcon: true });
    }
  };

  const renderNavLink = (menuList) => {
    if (!isLockerShop && isPreviewCatalogMode) {
      menuList = orderBy([...menuList], "forSwag");
    }
    if (isMobileDevice || !isMultiLevelMenuListingRequired)
      return menuList.map(renderSingleLevelMenuListing);
    if (isMultiLevelMenuListingRequired) {
      const allProductMenuItem = menuList.find((menuItem) => menuItem.code === ALL_PRODUCTS);
      return (
        <>
          {menuList.slice(0, SINGLE_LEVEL_MENU_THRESHOLD).map(renderSingleLevelMenuListing)}
          <aside
            className={`${ddStyles.mlmMenu} ${ddStyles.categoryDropdDown} ${ddStyles.storeNavLink}`}
          >
            <span className={ddStyles.seeMoreLink}>
              See more <i className={ddStyles.arrowIcon} />
            </span>
            <div
              className={`${ddStyles.categoryDropdDownMenu} ${ddStyles.categoryDropDownMenu} ${ddStyles.seeMoreDropDown}`}
            >
              {menuList
                .slice(SINGLE_LEVEL_MENU_THRESHOLD, menuListLength)
                .map(renderMultiLevelMenuListing)}
              {allProductMenuItem && (
                <div className={ddStyles.categoryAllLink}>
                  <InternalButton
                    className={ddStyles.navLink}
                    key={`navlink-${allProductMenuItem.id}`}
                    onClick={() => onDropdownItemClick(allProductMenuItem)}
                    onKeyDown={() => onDropdownItemClick(allProductMenuItem)}
                  >
                    <span>shop all products</span>
                    <i className={ddStyles.iconArrowRight} />
                  </InternalButton>
                </div>
              )}
            </div>
          </aside>
        </>
      );
    }
  };

  const renderEditCategoresCta = () =>
    isNewShopCreationFlow &&
    isProductListingPage && (
      <div className={ddStyles.editCtaContainer}>
        <InternalButton
          onClick={() => {
            trackEvent({
              componentName: isPricelessShop ? PRICELESS_SHOP : SHOP_CREATION,
              elementPosition: MENU_CATALOG_SELECTION,
              target: "edit",
            });
            openEditCategoriesModal();
          }}
          styleType="primary-outline"
        >
          <span className={ddStyles.editIcon} /> Edit
        </InternalButton>
        {isEditCategoriesModalOpen && (
          <DummyStoreCategories isModal closeModal={closeEditCategoriesModal} />
        )}
      </div>
    );

  return (
    <div
      className={`${ddStyles.navContainer} ${ddStyles.dFlexContainer} ${navContainerClass}  ${
        menuList.length < 4 ? ddStyles["menuLeftAlign"] : ddStyles["menuSpaceBetween"]
      }`}
    >
      {renderNavLink(menuList)}
      {children}
      {renderEditCategoresCta()}
    </div>
  );
};

export default Menu;
